import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button, Modal, Form, Input, message, Pagination } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined } from '@ant-design/icons';
import axios from 'axios';
import { SidebarLayout } from '../components/SidebarLayout';

const api = `${process.env.REACT_APP_API_URL}/customers`;


const applyCnpjMask = (value) => {
  if (!value) return value;

  const digits = value.replace(/\D/g, '');

  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
    .slice(0, 18);
};

const applyInsEstMask = (value) => {
  if (!value) return value;
  const digits = value.replace(/\D/g, '');
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .slice(0, 15);
};

const applyCepMask = (value) => {
  if (!value) return value;
  const digits = value.replace(/\D/g, '');
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1-$2')
    .slice(0, 10);
};

const validateCnpj = (cnpj) => {
  if (!cnpj) return false;
  
  const digits = cnpj.replace(/\D/g, '');
  
  return digits.length === 14;
};

const validateInsEst = (insEst) => {
  if (!insEst) return true;
  
  const digits = insEst.replace(/\D/g, '');
  
  return digits.length === 12;
};

const validateCep = (cep) => {
  if (!cep) return true;
  
  const digits = cep.replace(/\D/g, '');
  
  return digits.length === 8;
};

const CustomerForm = ({ visible, onCancel, onSubmit, initialValues }) => {
  const [form] = Form.useForm();
  useEffect(() => {
    if (visible) form.setFieldsValue(initialValues || {}); else form.resetFields();
  }, [visible, initialValues]);
  return (
    <Modal
      visible={visible}
      title={initialValues ? 'Editar Cliente' : 'Novo Cliente'}
      onCancel={onCancel}
      onOk={() => {
        form.validateFields()
          .then(values => {
            form.resetFields();
            onSubmit(values);
          });
      }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="dsname"
          label="Nome"
          rules={[
            { required: true, message: 'Nome é obrigatório' },
            { max: 255, message: 'Máximo de 255 caracteres' },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="dsfantasy"
          label="Nome Fantasia"
          rules={[
            { max: 255, message: 'Máximo de 255 caracteres' },
          ]}
        >
          <Input />
        </Form.Item>        <Form.Item
          name="nrcnpj"
          label="CNPJ"
          rules={[
            { required: true, message: 'CNPJ é obrigatório' },
            { 
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (validateCnpj(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('CNPJ inválido'));
              }
            },
          ]}
        >
          <Input 
            placeholder="00.000.000/0000-00"
            onChange={(e) => {
              const maskedValue = applyCnpjMask(e.target.value);
              form.setFieldsValue({ nrcnpj: maskedValue });
            }}
            maxLength={18}
          />
        </Form.Item>        <Form.Item
          name="nrinsest"
          label="Inscrição Estadual"
          rules={[
            { max: 30, message: 'Máximo de 30 caracteres' },
            { 
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (validateInsEst(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Inscrição Estadual deve ter 12 dígitos'));
              }
            },
          ]}
        >
          <Input 
            placeholder="000.000.000.000"
            onChange={(e) => {
              const maskedValue = applyInsEstMask(e.target.value);
              form.setFieldsValue({ nrinsest: maskedValue });
            }}
            maxLength={15}
          />
        </Form.Item>

        <Form.Item
          name="dscontact"
          label="Contato"
          rules={[
            { max: 255, message: 'Máximo de 255 caracteres' },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="dsaddress1"
          label="Endereço 1"
          rules={[
            { max: 255, message: 'Máximo de 255 caracteres' },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="dsaddress2"
          label="Endereço 2"
          rules={[
            { max: 255, message: 'Máximo de 255 caracteres' },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="dscity"
          label="Cidade"
          rules={[
            { max: 100, message: 'Máximo de 100 caracteres' },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="dsstate"
          label="Estado"
          rules={[
            { max: 2, message: 'Máximo de 2 caracteres' },
            { pattern: /^[A-Z]{2}$/, message: 'Use a sigla do estado (ex: SP)' },
          ]}
        >
          <Input maxLength={2} />
        </Form.Item>        <Form.Item
          name="dspostalcode"
          label="CEP"
          rules={[
            { max: 20, message: 'Máximo de 20 caracteres' },
            { 
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (validateCep(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('CEP deve ter 8 dígitos'));
              }
            },
          ]}
        >
          <Input 
            placeholder="00.000-000"
            onChange={(e) => {
              const maskedValue = applyCepMask(e.target.value);
              form.setFieldsValue({ dspostalcode: maskedValue });
            }}
            maxLength={10}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formVisible, setFormVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [messageApi, contextHolder] = message.useMessage();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(api);
      setCustomers(res.data);
    } catch {
      messageApi.error('Erro ao buscar clientes');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleCreate = async values => {
    try {
      await axios.post(api, values);
      messageApi.success('Cliente criado com sucesso');
      fetchCustomers();
    } catch (error) {
      var apiMsg = 'Erro ao criar cliente';
      if (error.response?.status != 500) {
        apiMsg +=  ': ' + error.response?.data?.error || '!';
      }
      messageApi.error(apiMsg);
    }
    setFormVisible(false);
  };

  const handleUpdate = async values => {
    try {
      await axios.put(`${api}/${editingCustomer.id}`, values);
      messageApi.success('Cliente atualizado com sucesso');
      fetchCustomers();
    } catch (error) {
      var apiMsg = 'Erro ao atualizar cliente';
      if (error.response?.status != 500) {
        apiMsg +=  ': ' + error.response?.data?.error || '!';
      }
      messageApi.error(apiMsg);
    }
    setEditingCustomer(null);
    setFormVisible(false);
  };

  const handleDelete = id => {
    Modal.confirm({
      title: 'Tem certeza que deseja remover este cliente?',
      content: 'Esta ação não poderá ser desfeita.',
      okText: 'Sim, remover',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await axios.delete(`${api}/${id}`);
          messageApi.success('Cliente removido com sucesso');
          fetchCustomers();
        } catch (error) {
          var apiMsg = 'Erro ao remover cliente';
          if (error.response?.status != 500) {
            apiMsg +=  ': ' + error.response?.data?.error || '!';
          }
          messageApi.error(apiMsg);
        }
      }
    });
  };

  const filteredCustomers = customers.filter(customer =>
    customer.dsname?.toLowerCase().includes(searchText.toLowerCase()) ||
    customer.nrcnpj?.toLowerCase().includes(searchText.toLowerCase()) ||
    customer.dsfantasy?.toLowerCase().includes(searchText.toLowerCase()) ||
    customer.dscontact?.toLowerCase().includes(searchText.toLowerCase())
  );

  const truncate = (text) => {
    if (!text) return '';
    return text.length > 28 ? text.slice(0, 28) + '...' : text;
  };

  return (
    <SidebarLayout>
      {contextHolder}
      <div style={{ padding: 24 }}>
        <h1><TeamOutlined style={{ fontSize: 24 }} /> Clientes</h1>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingCustomer(null);
              setFormVisible(true);
            }}
          >
            Novo
          </Button>
          <Input.Search
            placeholder="Buscar cliente"
            allowClear
            onSearch={value => setSearchText(value)}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
        </div>

        <Row gutter={[42, 32]}>
          {filteredCustomers
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map(customer => (
              <Col xs={24} sm={12} md={8} lg={8} key={customer.id}>
                <Card
                  actions={[
                    <EditOutlined key="edit" onClick={() => { setEditingCustomer(customer); setFormVisible(true); }} />,
                    <DeleteOutlined key="delete" style={{ color: 'red' }} onClick={() => handleDelete(customer.id)} />
                  ]}
                  style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', padding: 0 }}
                >
                  <p><b>Nome:</b> {truncate(customer.dsname)}</p>
                  <p><b>Nome Fantasia:</b> {truncate(customer.dsfantasy)}</p>
                  <p><b>CNPJ:</b> {truncate(customer.nrcnpj)}</p>
                  <p><b>Contato:</b> {truncate(customer.dscontact)}</p>
                  <p><b>Cidade:</b> {truncate(customer.dscity)}</p>
                  <p><b>Estado:</b> {truncate(customer.dsstate)}</p>
                </Card>
              </Col>
            ))}
        </Row>

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'end' }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={filteredCustomers.length}
            onChange={page => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </div>

        <CustomerForm
          visible={formVisible}
          onCancel={() => setFormVisible(false)}
          onSubmit={editingCustomer ? handleUpdate : handleCreate}
          initialValues={editingCustomer}
        />
      </div>
    </SidebarLayout>
  );
};

